import { type CSSObject } from '@emotion/react';
import { useTheme } from '../../../theme';

type Props = {
  active: number;
  count: number;
};

type Return = Record<
  'container' | 'tabContainer' | 'tab' | 'adjacentTab',
  CSSObject
>;

export const useSegmentedControlStyles = ({ active, count }: Props): Return => {
  const theme = useTheme();

  const offset = `calc(${theme.spacing(1)} / 2)`;

  const divider: CSSObject = {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '25%',
    bottom: '25%',
    width: 1,
    backgroundColor: theme.border.tertiary,
    opacity: 0,
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  };

  return {
    container: {
      padding: offset,
      borderRadius: `calc(${theme.spacing(2)} + 1.5px)`,
      backgroundColor: theme.segmentedControl.background,
    },
    tabContainer: {
      display: 'flex',
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      rowGap: offset,
      '&:before': {
        content: '""',
        position: 'absolute',
        left: `${(active * 100) / count}%`,
        width: `${100 / count}%`,
        height: '100%',
        border: `1px solid ${theme.segmentedControl.active.border}`,
        borderRadius: theme.shape.radius8,
        backgroundColor: theme.segmentedControl.active.background,
        transition: theme.transitions.create(['left'], {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
      },
    },
    tab: {
      display: 'flex',
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 1,
      columnGap: theme.spacing(2),
      position: 'relative',
      padding: theme.spacing(1),
      textAlign: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      overflow: 'hidden',
      fontWeight: theme.typography.fontWeightMedium,

      ':before': divider,

      ...theme.typography.bodyS,

      '&:hover:not([aria-selected="true"])': {
        borderRadius: theme.shape.radius8,
        backgroundColor: theme.segmentedControl.hover.background,
      },

      '&:active, &:focus': {
        borderColor: theme.button.outline.pressed,
      },

      '&:focus-visible': {
        outline: `2.5px solid ${theme.button.focused.default}`,
      },
    },

    adjacentTab: {
      ':before': {
        ...divider,
        opacity: 1,
      },
    },
  };
};
